import React, {useMemo} from 'react';
import {X} from 'lucide-react';
import okBadge from '../../assets/ok.png';
import firewallBadge from '../../assets/firewall.png';
import './NodePopover.css';

interface NodePopoverProps{
    isOpen: boolean;
    position: {x: number, y: number};
    zIndex: number;
    onClose: (e: React.MouseEvent) => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    label: string;
    description?: string;
    llm?: string;
    vulnerabilityCount: number;
    guardrails: string[];
    vulnerabilities: [];
    onVulnerabilityClick?: (vulnId: string) => void;
}

const NodePopover: React.FC<NodePopoverProps> = ({
    isOpen,
    position,
    zIndex,
    onClose,
    onMouseEnter,
    onMouseLeave,
    label,
    description,
    llm,
    vulnerabilityCount,
    guardrails,
    vulnerabilities,
    onVulnerabilityClick,
}) => {
    const hasVulnerability = vulnerabilityCount > 0;

    const dynamicStyle = useMemo(() => {
        const padding = 20;
        const availableHeight = position.y - padding;
        const maxHeight = Math.max(150, Math.min(availableHeight, 400));

        return {
            left: position.x,
            top: position.y,
            zIndex,
            maxHeight: `${maxHeight}px`,
        };
    }, [position.x, position.y, zIndex]);

    if (!isOpen) return null;

    const severityOrder = {
        'critical': 1,
        'high': 2,
        'medium': 3,
        'low': 4,
        'info': 5,
    };

    const sortedVulnerabilities = [...vulnerabilities].sort((a, b) => {
        const aSeverity = (a.risk_level || a.severity || 'Medium').toLowerCase();
        const bSeverity = (b.risk_level || b.severity || 'Medium').toLowerCase();
        return (severityOrder[aSeverity] || 99) - (severityOrder[bSeverity] || 99);
    });
    

    return (
        <div 
            className="node-popover"
            style={dynamicStyle}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="node-popover-header">
                <div className="node-popover-header-left">
                    <div className="node-popover-header-text">
                        <span className="node-popover-title">{label}</span>
                        {llm && <span className="node-popover-subtitle">LLM: {llm}</span>}
                    </div>
                </div>
                <button onClick={onClose} className="node-popover-close-btn">
                    <X size={16} />
                </button>
            </div>

            <div className="node-popover-content">
                {description && (
                    <p className="node-popover-description">{description} </p>
                )}

                <div className="node-popover-status">
                    <img 
                        src={hasVulnerability ? firewallBadge : okBadge}
                        alt=""
                        className="node-popover-status-icon"
                    />
                    <span className="node-popover-status-text">
                        {hasVulnerability
                        ? `${vulnerabilityCount} Potential Vulnerabilities Found`
                        : 'No vulnerabilities Detected'}
                    </span>
                </div>

                {hasVulnerability && vulnerabilityCount > 0 && Array.isArray(vulnerabilities) && vulnerabilities.length > 0 && (
                    <div className="node-popover-vulnerabilities">
                        <h4>Vulnerabilities</h4>
                        <ul className="node-popover-vuln-list">
                        {sortedVulnerabilities.map((vuln: any, idx: number) => {
                            // Map risk_level to class
                            const severity = (vuln.risk_level || vuln.severity || 'Medium').toLowerCase();
                            return (
                            <li 
                                key={vuln.id || idx} 
                                className="node-popover-vuln-item"
                                onClick={() => onVulnerabilityClick?.(vuln.id)}
                                style={{ cursor: 'pointer'}}
                            >
                                <span className="node-popover-vuln-name">{vuln.name}</span>
                                <span className={`vuln-card__severity-badge vuln-card__severity-badge--${severity}`}>
                                {vuln.risk_level || vuln.severity || 'Medium'}
                                </span>
                            </li>
                            );
                        })}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NodePopover;